import React, { useCallback, useEffect, useImperativeHandle, useState, forwardRef } from "react";
import { Box, Button, Grid, Spinner, Text, VStack } from "@chakra-ui/react";
import { apiClient } from '@/lib/api/client'
import { gridStyles } from './gridStyles'

export interface DataGridOptions {
  dataSource: string;
  primaryKey: string;
  itemsPerPage?: number;
  noRecordText?: string;
  renderCard: (item: any) => React.ReactNode;
  include?: string; 
}

export interface DataGridParams {
  search?: string;
  [key: string]: any;
}

export interface DataGridRef {
  reload: () => void;
}

interface DataGridProps {
  options: DataGridOptions;
  params?: DataGridParams;
  setTotal?: (total: number) => void;
  gridColumns?: {
    base?: string;
    sm?: string;
    md?: string;
    lg?: string;
    xl?: string;
    "2xl"?: string;
  };
}

const DataGrid = forwardRef<DataGridRef, DataGridProps>(
  ({ options, params = {}, setTotal, gridColumns }, ref) => {
    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    const itemsPerPage = options.itemsPerPage || 15;

    const defaultGridColumns = {
      base: "repeat(1, 1fr)",
      sm: "repeat(1, 1fr)",
      md: "repeat(2, 1fr)",
      lg: "repeat(2, 1fr)",
      xl: "repeat(3, 1fr)",
      "2xl": "repeat(4, 1fr)",
    };

    const fetchData = useCallback(
      async (page: number, append: boolean = false) => {
        setIsLoading(true);
        try {
          const response = await apiClient.get(options.dataSource, {
            params: {
              ...params,
              page,
              per_page: itemsPerPage,
              ...(options.include && { include: options.include }),
            },
          });

          const newData = response.data.data || [];
          const total = response.data.meta?.all || response.data.meta?.total || 0;

          setTotalRecords(total);
          if (setTotal) {
            setTotal(total);
          }

          if (append) {
            setData((prev) => [...prev, ...newData]);
          } else {
            setData(newData);
          }

          // Check if there are more records
          const currentTotal = append ? data.length + newData.length : newData.length;
          setHasMore(currentTotal < total);
        } catch (error) {
          console.error("Error fetching grid data:", error);
          setData([]);
          setTotalRecords(0);
          setHasMore(false);
          if (setTotal) {
            setTotal(0);
          }
        } finally {
          setIsLoading(false);
        }
      },
      [options.dataSource, options.include, params, itemsPerPage, setTotal, data.length]
    );

    const paramsKey = JSON.stringify(params);
    useEffect(() => {
      setCurrentPage(1);
      fetchData(1, false);
    }, [paramsKey, options.dataSource]);

    const handleShowMore = () => {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchData(nextPage, true);
    };

    const reload = useCallback(() => {
      setCurrentPage(1);
      fetchData(1, false);
    }, [fetchData]);

    useImperativeHandle(ref, () => ({
      reload,
    }));

    if (isLoading && data.length === 0) {
      return (
        <VStack {...gridStyles.loading.container}>
          <Spinner {...gridStyles.loading.spinner} />
          <Text {...gridStyles.loading.text}>Loading...</Text>
        </VStack>
      );
    }

    if (!isLoading && data.length === 0) {
      return (
        <Box {...gridStyles.empty.container}>
          <Text {...gridStyles.empty.text}>
            {options.noRecordText || "No records found"}
          </Text>
        </Box>
      );
    }

    return (
      <VStack {...gridStyles.container}>
        {/* Grid Container */}
        <Box {...gridStyles.gridContainer}>
          <Grid
            templateColumns={gridColumns || defaultGridColumns}
            {...gridStyles.grid}
          >
            {data.map((item) => (
              <React.Fragment key={item[options.primaryKey]}>
                {options.renderCard(item)}
              </React.Fragment>
            ))}
          </Grid>

          {/* Show More Button - Inside grid container */}
          {hasMore && (
            <Box {...gridStyles.loadMore.container}>
              <Button
                onClick={handleShowMore}
                isLoading={isLoading}
                spinner={<Spinner size="sm" color="#1A202C" thickness="2px" />}
                {...gridStyles.loadMore.button}
              >
                Load More
              </Button>
            </Box>
          )}

          {/* All Loaded Message */}
          {!hasMore && data.length > 0 && data.length === totalRecords && (
            <Box {...gridStyles.allLoaded.container}>
              <Text {...gridStyles.allLoaded.text}>
                All {totalRecords} records loaded
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
    );
  }
);

DataGrid.displayName = "DataGrid";

export default DataGrid;